import { useState, useEffect, useRef } from 'react'
import './GameRoomSidebar.css'

export interface GameState {
  phase?: string
  turn?: string
  timer?: number
  score?: Record<string, number | string>
}

export interface Message {
  sender: 'you' | 'opponent' | 'ai' | 'system'
  content: string
  timestamp: number
}

export interface HistoryItem {
  type: 'question' | 'answer' | 'move' | 'event'
  content: string
  timestamp: number
}

export interface GameRoomSidebarProps {
  mode: 'ai' | 'network'
  gameState: GameState
  messages: Message[]
  historyItems?: HistoryItem[]
  onSendMessage: (message: string) => void
  onToggleCollapse?: () => void
  collapsed?: boolean
  position?: 'docked' | 'floating' | 'overlay' | 'bottom-sheet'
  quickReactions?: boolean
}

export function GameRoomSidebar({
  mode,
  gameState,
  messages,
  historyItems = [],
  onSendMessage,
  onToggleCollapse,
  collapsed = false,
  position = 'docked',
  quickReactions = true
}: GameRoomSidebarProps) {
  const [inputValue, setInputValue] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)

  // Swipe gesture state for bottom sheet
  const [isDragging, setIsDragging] = useState(false)
  const [startY, setStartY] = useState(0)
  const sidebarRef = useRef<HTMLElement>(null)

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages])

  // Swipe gesture handlers for bottom sheet
  useEffect(() => {
    if (position !== 'bottom-sheet' || !sidebarRef.current || !onToggleCollapse) return

    const sidebar = sidebarRef.current

    const handleTouchStart = (e: TouchEvent) => {
      setIsDragging(true)
      setStartY(e.touches[0].clientY)
      sidebar.classList.add('dragging')
    }

    const handleTouchMove = (e: TouchEvent) => {
      if (!isDragging) return

      const currentY = e.touches[0].clientY
      const deltaY = currentY - startY

      // Prevent default scrolling while dragging
      if (Math.abs(deltaY) > 10) {
        e.preventDefault()
      }
    }

    const handleTouchEnd = (e: TouchEvent) => {
      if (!isDragging) return

      const currentY = e.changedTouches[0].clientY
      const deltaY = currentY - startY
      const threshold = 50 // Minimum swipe distance

      sidebar.classList.remove('dragging')
      setIsDragging(false)

      // Swipe down to collapse, swipe up to expand
      if (Math.abs(deltaY) > threshold) {
        if (deltaY > 0 && !collapsed) {
          // Swiped down - collapse
          onToggleCollapse()
        } else if (deltaY < 0 && collapsed) {
          // Swiped up - expand
          onToggleCollapse()
        }
      }
    }

    // Tap anywhere on collapsed sidebar to expand
    const handleClick = (e: MouseEvent) => {
      if (!collapsed) return
      // Don't trigger if clicking input or button
      const target = e.target as HTMLElement
      if (target.tagName === 'INPUT' || target.tagName === 'BUTTON' || target.closest('button')) {
        return
      }
      onToggleCollapse()
    }

    sidebar.addEventListener('touchstart', handleTouchStart, { passive: false })
    sidebar.addEventListener('touchmove', handleTouchMove, { passive: false })
    sidebar.addEventListener('touchend', handleTouchEnd)
    sidebar.addEventListener('click', handleClick)

    return () => {
      sidebar.removeEventListener('touchstart', handleTouchStart)
      sidebar.removeEventListener('touchmove', handleTouchMove)
      sidebar.removeEventListener('touchend', handleTouchEnd)
      sidebar.removeEventListener('click', handleClick)
    }
  }, [position, isDragging, startY, collapsed, onToggleCollapse])

  const handleSend = () => {
    if (!inputValue.trim()) return

    onSendMessage(inputValue.trim())
    setInputValue('')

    // Refocus input
    inputRef.current?.focus()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  const handleQuickReaction = (reaction: string) => {
    onSendMessage(reaction)
  }

  const formatTime = (timestamp: number) => {
    const date = new Date(timestamp)
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  }

  const getSenderLabel = (sender: Message['sender']) => {
    switch (sender) {
      case 'you': return 'You'
      case 'opponent': return 'Opponent'
      case 'ai': return '🤖 AI'
      case 'system': return 'System'
    }
  }

  const getSenderClass = (sender: Message['sender']) => {
    return `message-${sender}`
  }

  // Render floating button when collapsed (for floating and overlay, but not bottom-sheet)
  if (collapsed && (position === 'floating' || position === 'overlay')) {
    const unreadCount = 0 // TODO: Implement unread tracking
    return (
      <button
        className="sidebar-toggle-button"
        onClick={onToggleCollapse}
        aria-label="Open chat"
      >
        💬
        {unreadCount > 0 && (
          <span className="unread-badge">{unreadCount}</span>
        )}
      </button>
    )
  }

  // Bottom sheet is always visible (collapsed or expanded)
  const isBottomSheet = position === 'bottom-sheet'
  const sidebarClasses = `game-room-sidebar ${position} ${isBottomSheet ? 'bottom-sheet' : ''} ${collapsed ? 'collapsed' : 'expanded'}`

  return (
    <aside ref={sidebarRef} className={sidebarClasses}>
      {/* Header */}
      <div className="sidebar-header">
        <div className="sidebar-title">
          <span className="title-icon">💬</span>
          <h2 className="title-text">
            {mode === 'ai' ? 'Q&A with AI' : 'Chat'}
          </h2>
        </div>

        <div className="sidebar-actions">
          {historyItems.length > 0 && (
            <button
              className="history-button"
              onClick={() => setShowHistory(!showHistory)}
              aria-label={`${showHistory ? 'Hide' : 'Show'} history`}
            >
              📜 History ({historyItems.length})
            </button>
          )}

          {onToggleCollapse && position !== 'docked' && (
            <button
              className="close-button"
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                console.log('[GameRoomSidebar] Close button clicked')
                onToggleCollapse()
              }}
              aria-label="Close sidebar"
              type="button"
            >
              ✕
            </button>
          )}
        </div>
      </div>

      {/* Game State Panel */}
      {(gameState.phase || gameState.turn || gameState.timer !== undefined || gameState.score) && (
        <div className="game-state-panel">
          {gameState.phase && (
            <div className="state-item">
              <span className="state-label">Phase:</span>
              <span className="state-value">{gameState.phase}</span>
            </div>
          )}

          {gameState.turn && (
            <div className="state-item turn-item">
              <span className="state-label">Turn:</span>
              <span className="state-value">{gameState.turn}</span>
            </div>
          )}

          {gameState.timer !== undefined && (
            <div className="state-item timer-item">
              <span className="state-label">⏱️</span>
              <span className="state-value timer-value">
                {Math.floor(gameState.timer / 60)}:{String(gameState.timer % 60).padStart(2, '0')}
              </span>
            </div>
          )}

          {gameState.score && (
            <div className="state-item score-item">
              <span className="state-label">🏆</span>
              <span className="state-value">
                {Object.entries(gameState.score).map(([player, score]) => (
                  <span key={player} className="score-entry">
                    {player}: {score}
                  </span>
                ))}
              </span>
            </div>
          )}
        </div>
      )}

      {/* History Panel (when open) */}
      {showHistory && (
        <div className="history-panel">
          <div className="history-header">
            <h3>History</h3>
            <button onClick={() => setShowHistory(false)}>✕</button>
          </div>
          <div className="history-content">
            {historyItems.length === 0 ? (
              <p className="empty-state">No history yet</p>
            ) : (
              historyItems.map((item, index) => (
                <div key={index} className={`history-item ${item.type}`}>
                  <span className="history-time">{formatTime(item.timestamp)}</span>
                  <span className="history-content">{item.content}</span>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Messages Feed */}
      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="empty-state">
            <p>
              {mode === 'ai'
                ? 'Ask me questions to find your mystery character!'
                : 'Start chatting with your opponent'}
            </p>
          </div>
        ) : (
          <div className="messages-list">
            {messages.map((msg, index) => (
              <div key={index} className={`message ${getSenderClass(msg.sender)}`}>
                <div className="message-header">
                  <span className="sender-name">{getSenderLabel(msg.sender)}</span>
                  <span className="message-time">{formatTime(msg.timestamp)}</span>
                </div>
                <div className="message-content">{msg.content}</div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      {/* Input Area */}
      <div className="input-container">
        {mode === 'network' && quickReactions && (
          <div className="quick-reactions">
            {['👍', '❤️', '😂', '🎉'].map((reaction) => (
              <button
                key={reaction}
                className="reaction-button"
                onClick={() => handleQuickReaction(reaction)}
                aria-label={`Send ${reaction}`}
              >
                {reaction}
              </button>
            ))}
          </div>
        )}

        <div className="input-row">
          <input
            ref={inputRef}
            type="text"
            className="message-input"
            placeholder={
              mode === 'ai'
                ? 'Type your question...'
                : 'Type a message...'
            }
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            className="send-button"
            onClick={handleSend}
            disabled={!inputValue.trim()}
            aria-label="Send message"
          >
            →
          </button>
        </div>

        {mode === 'ai' && (
          <div className="input-hint">
            <small>Example: "Does your person have glasses?"</small>
          </div>
        )}
      </div>
    </aside>
  )
}
